import api from "../../config/Api";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

export interface Address {
    id?: number;
    name: string;
    locality: string;
    address: string;
    city: string;
    state: string;
    pinCode: string;
    mobile: string;
}

interface AddressState {
    addresses: Address[];
    loading: boolean;
    error: string | null;
    success: boolean;
}

const API_URL = "/api/addresses";

const getAuthHeader = (jwt: string) => {
    const cleanToken = jwt.trim();
    return cleanToken.startsWith("Bearer ") ? cleanToken : `Bearer ${cleanToken}`;
};

export const fetchUserAddresses = createAsyncThunk<Address[], string>(
    "address/fetchUserAddresses",
    async (jwt, { rejectWithValue }) => {
        try {
            if (!jwt) {
                return rejectWithValue("JWT Token missing.");
            }

            const response = await api.get<Address[]>(API_URL, {
                headers: { Authorization: getAuthHeader(jwt) },
            });

            return Array.isArray(response.data) ? response.data : [];
        } catch (error: any) {
            const serverErrorMessage =
                error.response?.data?.message || error.response?.data || "Failed to fetch addresses";
            return rejectWithValue(serverErrorMessage);
        }
    }
);

export const createUserAddress = createAsyncThunk<Address, { jwt: string; address: Address }>(
    "address/createUserAddress",
    async ({ jwt, address }, { rejectWithValue }) => {
        try {
            if (!jwt) {
                return rejectWithValue("JWT Token missing from local storage.");
            }

            const payload: Address = {
                name: address.name?.trim() || "",
                mobile: address.mobile?.trim() || "",
                pinCode: address.pinCode?.trim() || "",
                address: address.address?.trim() || "",
                city: address.city?.trim() || "",
                locality: address.locality?.trim() || address.address?.trim() || "N/A",
                state: address.state?.trim() || address.city?.trim() || "N/A",
            };

            const response = await api.post<Address>(API_URL, payload, {
                headers: {
                    Authorization: getAuthHeader(jwt),
                    "Content-Type": "application/json",
                },
            });

            return response.data;
        } catch (error: any) {
            const serverErrorMessage =
                error.response?.data?.message ||
                (typeof error.response?.data === "string" ? error.response?.data : "Failed to create address");
            return rejectWithValue(serverErrorMessage);
        }
    }
);

const initialState: AddressState = {
    addresses: [],
    loading: false,
    error: null,
    success: false,
};

const addressSlice = createSlice({
    name: "address",
    initialState,
    reducers: {
        clearAddressError: (state) => {
            state.error = null;
        },
        resetAddressSuccess: (state) => {
            state.success = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserAddresses.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserAddresses.fulfilled, (state, action: PayloadAction<Address[]>) => {
                state.loading = false;
                state.addresses = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchUserAddresses.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(createUserAddress.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createUserAddress.fulfilled, (state, action: PayloadAction<Address>) => {
                state.loading = false;
                state.success = true;
                if (action.payload && action.payload.id) {
                    const exists = state.addresses.some((addr) => addr.id === action.payload.id);
                    if (!exists) {
                        state.addresses.push(action.payload);
                    }
                }
            })
            .addCase(createUserAddress.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload;
                state.success = false;
            });
    },
});

export const { clearAddressError, resetAddressSuccess } = addressSlice.actions;
export default addressSlice.reducer;